import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { searchFaces } from '../services/api';
import { assetUrl } from '../utils/imageUrls';


function ImageUpload({ onResultsChange }) {
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [error, setError] = useState('');
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  useEffect(() => (
    () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    }
  ), [previewUrl]);

  const handleGalleryClick = () => {
    setIsPickerOpen(false);
    galleryInputRef.current?.click();
  };

  const handleCameraClick = () => {
    setIsPickerOpen(false);
    cameraInputRef.current?.click();
  };

  const handleUploadClick = () => {
    setIsPickerOpen(true);
  };

  const handleChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(URL.createObjectURL(file));
    setLoading(true);
    setNoResults(false);
    setError('');
    onResultsChange([]);

    try {
      const data = await searchFaces(file);
      onResultsChange(data || []);
      setNoResults(!data || data.length === 0);
    } catch (err) {
      setError(err.message || 'Unable to search this image.');
      onResultsChange([]);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        textAlign: 'center',
      }}
    >
      <input
        ref={galleryInputRef}
        accept="image/*"
        style={{ display: 'none' }}
        type="file"
        onChange={handleChange}
      />
      <input
        ref={cameraInputRef}
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        type="file"
        onChange={handleChange}
      />

      <Box
        component="button"
        type="button"
        onClick={handleUploadClick}
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.2s ease',
          marginBottom: '20px',
          border: 0,
          background: 'transparent',
          padding: 0,
          '&:hover': {
            transform: 'scale(1.02)',
          },
        }}
      >
        <img
          src={previewUrl || assetUrl('electronics (1).png')}
          alt="Upload Face Photo"
          style={{
            maxWidth: '150px',
            height: 'auto',
            borderRadius: previewUrl ? '8px' : undefined,
            boxShadow: previewUrl ? '0 2px 8px rgba(0,0,0,0.2)' : undefined,
          }}
        />
      </Box>

      {isPickerOpen && (
        <div
          className="upload-picker-backdrop"
          onClick={() => setIsPickerOpen(false)}
        >
          <div
            className="upload-picker-card"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="upload-picker-close"
              title="Close"
              onClick={() => setIsPickerOpen(false)}
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div className="upload-picker-header">
              <span>Upload your photo</span>
              <p>Choose a saved image or take a new one now.</p>
            </div>
            <div className="upload-picker-actions">
              <button type="button" onClick={handleCameraClick}>
                <span className="upload-picker-icon">
                  <i className="fa-solid fa-camera" />
                </span>
                <span>
                  <strong>Camera</strong>
                  <small>Take instant photo</small>
                </span>
              </button>
              <button type="button" onClick={handleGalleryClick}>
                <span className="upload-picker-icon">
                  <i className="fa-solid fa-images" />
                </span>
                <span>
                  <strong>Gallery</strong>
                  <small>Choose from device</small>
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <Box sx={{ mt: 2 }}>
          <CircularProgress size={24} sx={{ color: '#ffffff' }} />
        </Box>
      )}

      {noResults && (
        <Typography
          sx={{
            mt: 2,
            color: '#ffffff',
            opacity: 0.8,
            fontSize: '0.9rem',
          }}
        >
          No similar faces found
        </Typography>
      )}

      {error && (
        <Typography className="upload-error">
          {error}
        </Typography>
      )}
    </Box>
  );
}

export default ImageUpload;
